"use client";
import Button from "@/components/Button";
import LanguageChooser from "@/components/LanguageChooser";
import { useAppContext } from "@/context/AppContext";
import { useUserContext } from "@/context/UserContext";
import { cn } from "@/lib/utils";
import {
  faDiscord,
  faFacebookSquare,
  faInstagram,
  faTiktok,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { LogInIcon, MenuIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { showPreferences } from "vanilla-cookieconsent";

export default function MainLayout({ children }) {
  const originalPathname = usePathname();
  const { locale, alternate } = useAppContext();
  const pathname = originalPathname.replace("/" + locale, "") || "/";
  const { status } = useUserContext();
  const { push } = useRouter();

  const { t } = useTranslation();

  const { authModal } = useUserContext();
  const [menuOpen, setMenuOpen] = useState(false);

  const selected = pathname;
  const menus = [
    {
      name: t("microsite"),
      href: "/microsite",
    },
    {
      name: t("shortener"),
      href: "/shortener",
    },
    {
      name: t("shop"),
      href: "/shop",
    },
    {
      name: t("qr_code"),
      href: "/qr-code",
    },
    {
      name: t("subscription"),
      href: "/subscription",
    },
  ];

  const footers = [
    {
      heading: t("services"),
      menus: [
        { label: t("microsite_builder"), href: "/microsite" },
        { label: t("url_shortener"), href: "/shortener" },
        { label: t("qr_code"), href: "/qr-code" },
        { label: "Shop", href: "/shop" },
        { label: t("subscription"), href: "/subscription" },
      ],
    },
    {
      heading: t("help_support"),
      menus: [
        { label: "Helpdesk", href: "https://s.id/help", target: "_blank" },
        { label: "Report Links", href: "/page/report" },
        { label: "Status", href: "https://s.id/status", target: "_blank" },
        // { label: "Trust & Safety", href: "/page/trust-safety" },
      ],
    },
    {
      heading: t("legal"),
      menus: [
        {
          label: t("terms_of_service"),
          href: "/page/terms-of-service",
        },
        {
          label: t("privacy_policy"),
          href: "/page/privacy-policy",
        },
      ],
    },
    {
      heading: t("external"),
      menus: [
        {
          label: t("blog"),
          href: alternate
            ? `https://${alternate}/?utm_source=sid-footer`
            : "https://blog.s.id?utm_source=sid-footer",
        },
        {
          label: t("sponsorship"),
          href: "/page/sponsorship",
        },
      ],
    },
  ];

  const socmeds = [
    {
      slug: "instagram",
      href: "https://instagram.com/home.s.id",
      icon: <FontAwesomeIcon icon={faInstagram} className="size-8" />,
    },
    {
      slug: "twitter",
      href: "https://twitter.com/home_s_id",
      icon: <FontAwesomeIcon icon={faXTwitter} className="size-8" />,
    },
    {
      slug: "facebook",
      href: "https://facebook.com/socmed.s.id",
      icon: <FontAwesomeIcon icon={faFacebookSquare} className="size-8" />,
    },
    {
      slug: "tiktok",
      href: "https://tiktok.com/@home.s.id",
      icon: <FontAwesomeIcon icon={faTiktok} className="size-8" />,
    },
    {
      slug: "discord",
      href: "https://s.id/discord",
      icon: <FontAwesomeIcon icon={faDiscord} className="size-8" />,
    },
  ];

  return (
    <>
      <header className="top-0 right-0 left-0 z-50 fixed bg-background/50 backdrop-blur-md">
        <div className="relative flex mx-auto p-4 w-full max-w-[100rem] shrink-0">
          <Link href="/" className="z-10 relative">
            <img src="/images/sid-neu-logo-dark.svg" className="h-10" />
          </Link>
          <div className="flex-1"></div>
          <div className="hidden absolute inset-0 lg:flex flex-1 justify-center items-center">
            <ul className="flex justify-center items-center gap-2">
              {menus.map((menu) => (
                <li key={menu.name}>
                  <Link
                    href={menu.href}
                    className={cn(
                      "transition-all",
                      " py-2 rounded-full font-worksans font-medium",
                      selected === menu.href
                        ? "bg-primary text-white px-6"
                        : "px-4 hover:bg-white"
                    )}
                  >
                    {menu.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="mr-2">
            <LanguageChooser
              light
              simple
              className="!py-2 !pl-4 !rounded-full"
            />
          </div>
          <div className="hidden lg:block z-10 relative">
            <Button
              roundFull
              variant="ghost"
              className="relative flex items-center bg-primary hover:bg-primary/80 pl-4 border border-primary ring-4 ring-primary/20 font-worksans font-medium text-white hover:text-white truncate"
              onClick={() => {
                if (status === "authenticated") {
                  push("/dashboard");
                } else {
                  authModal.open();
                }
              }}
            >
              {status === "authenticated" ? (
                <Trans i18nKey="dashboard">Dashboard</Trans>
              ) : (
                <Trans i18nKey="login_register">Login / Register</Trans>
              )}

              <LogInIcon className="size-4" />
            </Button>
          </div>
          <div className="lg:hidden z-10 relative ml-3">
            <Button
              variant="outline"
              className="bg-white/80 border text-foreground"
              roundFull
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon className="-mx-1 size-6" />
            </Button>
          </div>
        </div>
      </header>

      <div
        className={cn(
          "transition-all",
          "lg:hidden z-50 fixed inset-0 bg-black/50",
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMenuOpen(false)}
      />
      <div
        className={cn(
          "transition-all",
          "lg:hidden right-0 z-50 top-0 fixed bg-white p-4 sm:rounded-l-3xl w-full max-w-xl h-screen",
          menuOpen
            ? "translate-x-0 opacity-100"
            : "translate-x-5 pointer-events-none opacity-0"
        )}
      >
        <div className="flex justify-end mb-4">
          <Button
            roundFull
            variant="primary"
            className="flex justify-center items-center w-full text-center"
            onClick={() => {
              authModal.open();
            }}
          >
            <Trans i18nKey="goto_dashboard">Go to Dashboard</Trans>
            <LogInIcon className="ml-2 size-4" />
          </Button>

          <button
            onClick={() => setMenuOpen(false)}
            className="flex justify-center items-center ml-3 border rounded-full size-10 shrink-0"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        <ul className="flex flex-col gap-2">
          <li className="pb-2 border-b">
            <Link
              href="/"
              onClick={() => setMenuOpen(false)}
              className={cn(
                "transition-all",
                "block py-2 text-xl font-worksans font-medium w-full",
                selected === "/"
                  ? "text-primary font-semibold"
                  : "text-foreground/70"
              )}
            >
              {t("home")}
            </Link>
          </li>

          {menus.map((menu) => (
            <li key={menu.name} className="pb-2 border-b">
              <Link
                href={menu.href}
                onClick={() => setMenuOpen(false)}
                className={cn(
                  "transition-all",
                  "block py-2 text-xl font-worksans font-medium w-full",
                  selected === menu.href
                    ? "text-primary font-semibold"
                    : "text-foreground/70"
                )}
              >
                {menu.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {children}
      <div className="mx-auto lg:px-4 max-w-[100rem]">
        <footer className="flex flex-col gap-3 lg:gap-0 bg-muted/20 mt-10 p-6 pb-0 border border-b-0 lg:rounded-t-3xl min-h-[20rem]">
          <div className="flex lg:flex-row flex-col flex-1 gap-3 lg:gap-0">
            <div className="order-2 lg:order-1 pr-6 w-full max-w-xs">
              <img src="/images/sid-neu-logo-dark.svg" className="h-10" />
              <p className="mt-4 text-foreground/60 text-sm">
                s.id by <strong>PT Aidi Digital Global</strong>
              </p>
              <address className="mt-2 text-foreground/50 text-xs not-italic">
                <strong>Icon Business Park</strong>, <br />
                Jl. BSD Raya Barat unit T05, Sampora, City,
                <br />
                Kabupaten Tangerang, Banten 15345
              </address>

              <div className="mt-2 text-foreground/50 text-xs">
                <a href="mailto:helpdesk@s.id" className="underline">
                  <EnvelopeIcon className="inline-block mr-1 size-4" />
                  helpdesk@s.id
                </a>
              </div>

              <div className="flex mt-4">
                {socmeds.map((soc) => (
                  <a
                    key={soc.slug}
                    href={soc.href}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="flex justify-center items-center bg-black hover:bg-white mx-1 border border-black rounded-full size-10 text-white hover:text-black"
                    aria-label={soc.slug}
                  >
                    {soc.icon}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex lg:flex-row flex-col gap-4 lg:gap-0 order-1 lg:order-2 w-full max-w-4xl">
              {footers.map((footer) => (
                <div key={footer.heading} className="flex-1">
                  <h3 className="mb-1 font-worksans font-bold">
                    {footer.heading}
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {footer.menus.map((menu) => (
                      <li
                        key={menu.label}
                        className="text-foreground/60 text-sm"
                      >
                        <Link
                          href={menu.href}
                          className="hover:underline"
                          target={menu.target}
                        >
                          {menu.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="flex lg:flex-row flex-col items-center border-t">
            <div className="flex-1 py-3 text-foreground/50 text-sm">
              &copy; {new Date().getFullYear()} s.id
            </div>
            <div className="flex items-center py-3">
              <a
                href="#"
                className="mr-3 text-xs underline"
                onClick={(e) => {
                  e.preventDefault();
                  showPreferences();
                }}
              >
                Manage Cookies
              </a>
              <LanguageChooser light />
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
